<ct-lib-modal
  #modal
  [label]="modalTitleKey() | transloco"
  [closeable]="!bookingInProgress()"
  [hideFooter]="isPaymentStep() && paymentCheckout()?.loading() === true"
  [footer]="combinedBookingFooter"
  (onClose)="onModalClosed()"
>
  <div class="combined-booking-modal">
    @if (isPaymentStep()) {
      <div class="checkout-header" aria-live="polite">
        <p>
          {{
            'company-tool.snippets.combined-subscription-booking-modal.checkout.pay'
              | transloco
          }}
        </p>
      </div>
      <app-payment-checkout
        (paymentCompleted)="onPaymentCompleted()"
        (paymentFailed)="backToSelection()"
      ></app-payment-checkout>
    } @else if (isPaymentMethodStep()) {
      <app-payment-method-choice
        #paymentMethodChoice
        [busy]="bookingInProgress()"
      />
    } @else {
      <p class="intro">
        {{ introKey() | transloco }}
      </p>

      <ct-lib-segmented-control
        class="billing-interval"
        [autowidth]="false"
        [items]="billingIntervalOptions"
        [selectedValue]="billingInterval()"
        (valueChange)="setBillingInterval($event)"
      ></ct-lib-segmented-control>

      @if (catalogsLoading()) {
        <div class="loading-wrapper" aria-live="polite">
          <ct-lib-spinner [size]="40"></ct-lib-spinner>
          <span>
            {{
              'company-tool.snippets.combined-subscription-booking-modal.loading'
                | transloco
            }}
          </span>
        </div>
      } @else {
        <section class="booking-section" aria-labelledby="base-section-title">
          <div class="section-header">
            <h3 id="base-section-title">
              {{
                'company-tool.snippets.combined-subscription-booking-modal.sections.base'
                  | transloco
              }}
            </h3>
          </div>

          <app-base-plan-selector
            [products]="baseProducts()"
            [loaded]="true"
            [billingInterval]="billingInterval()"
            [disabled]="selectionLocked()"
            [currentPriceId]="currentBaseCatalogPriceId()"
            [(selectedPriceId)]="selectedBasePriceId"
          />
        </section>

        <section
          class="booking-section optional"
          aria-labelledby="agents-section-title"
        >
          <label
            class="section-header selectable"
            [class.selected]="includeAgents()"
          >
            <input
              type="checkbox"
              [checked]="includeAgents()"
              [disabled]="selectionLocked()"
              (change)="setIncludeAgents($event)"
            />
            <span class="section-title" id="agents-section-title">
              {{
                'company-tool.snippets.combined-subscription-booking-modal.sections.agents'
                  | transloco
              }}
            </span>
          </label>

          @if (includeAgents()) {
            <div class="section-body" @collapse>
              <app-agents-plan-selector
                [products]="agentsProducts()"
                [loaded]="true"
                [billingInterval]="billingInterval()"
                [disabled]="selectionLocked()"
                [isCustomCurrent]="agentsIsCustomCurrent()"
                [currentPackagePriceId]="currentAgentsPackagePriceId()"
                [(selectedOption)]="selectedAgentsOption"
                [(customAgentCount)]="customAgentCount"
              />
            </div>
          }
        </section>

        <section
          class="booking-section optional"
          aria-labelledby="ats-section-title"
        >
          <label
            class="section-header selectable"
            [class.selected]="includeAts()"
          >
            <input
              type="checkbox"
              [checked]="includeAts()"
              [disabled]="selectionLocked()"
              (change)="setIncludeAts($event)"
            />
            <span class="section-title" id="ats-section-title">
              {{
                'company-tool.snippets.combined-subscription-booking-modal.sections.ats'
                  | transloco
              }}
            </span>
          </label>

          @if (includeAts()) {
            <div class="section-body" @collapse>
              <app-ats-addon-selector
                [products]="atsProducts()"
                [loaded]="true"
                [billingInterval]="billingInterval()"
                [disabled]="selectionLocked()"
                [(selectedPriceId)]="selectedAtsPriceId"
              />
            </div>
          }
        </section>

        @if (hasPaidPackage()) {
          <ct-lib-info-banner type="info" class="proration-hint" role="status">
            <p>
              {{
                'company-tool.snippets.combined-subscription-booking-modal.proration-hint'
                  | transloco
              }}
            </p>
          </ct-lib-info-banner>
        }

        <div class="summary" aria-live="polite">
          <span>
            {{
              'company-tool.snippets.combined-subscription-booking-modal.total'
                | transloco
            }}
          </span>
          <strong>
            {{ monthlyTotal() | currency: 'EUR' : 'symbol' : '1.0-0' }}
            {{ totalPeriodHint() }}
          </strong>
        </div>
      }
    }
  </div>
</ct-lib-modal>

<ng-template #combinedBookingFooter>
  @if (isPaymentStep()) {
    <app-button variant="white-grey" (click)="backToSelection()">
      {{
        'company-tool.snippets.combined-subscription-booking-modal.back'
          | transloco
      }}
    </app-button>
  } @else if (isPaymentMethodStep()) {
    <app-button variant="white-grey" (click)="backToSelection()">
      {{
        'company-tool.snippets.combined-subscription-booking-modal.back'
          | transloco
      }}
    </app-button>
    <app-button
      [disabled]="!canBook()"
      [loading]="bookingInProgress()"
      (click)="confirmPaymentMethod()"
    >
      {{ confirmLabelKey() | transloco }}
    </app-button>
  } @else {
    <app-button variant="white-grey" (click)="close()">
      {{
        'company-tool.snippets.combined-subscription-booking-modal.cancel'
          | transloco
      }}
    </app-button>
    <app-button
      [disabled]="!canBook()"
      [loading]="bookingInProgress()"
      (click)="confirmBooking()"
    >
      {{ confirmLabelKey() | transloco }}
    </app-button>
  }
</ng-template>

<app-invoice-booking-confirm-modal
  #invoiceConfirm
  (confirmed)="onInvoiceBookingConfirmed()"
  (dismissed)="selectionLocked.set(false)"
></app-invoice-booking-confirm-modal>

<ct-lib-confirm-modal
  #downgradeConfirmModal
  [disableConfirmButton]="bookingInProgress()"
  (onConfirm)="confirmLaterChanges()"
  (onCancel)="selectionLocked.set(false)"
  [label]="
    'company-tool.snippets.combined-subscription-booking-modal.later-confirm.title'
      | transloco
  "
>
  {{
    'company-tool.snippets.combined-subscription-booking-modal.later-confirm.text'
      | transloco
        : {
            date: (laterChangeDate() | date: 'longDate') || '—',
          }
  }}
</ct-lib-confirm-modal>
